import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { UsersApiAdapter } from '../adapters/UsersApiAdapter';
import { Service } from '../services/Service';

const PENDING_INVITATIONS_QUERY_KEY = ['pending-invitations'] as const;

// 🎯 Flag global para marcar cuando se está cancelando una invitación
// Esto evita que se detecte como una aceptación cuando se cancela
// Se importa desde useCancelInvitation
declare global {
  var __isCancellingInvitation: boolean | undefined;
}

/**
 * Hook para obtener invitaciones pendientes del equipo
 * @param enablePolling - Si debe hacer polling activo (cada 3 segundos)
 * @param onInvitationAccepted - Callback cuando se detecta que una invitación fue aceptada
 */
export const usePendingInvitations = (
  enablePolling: boolean = false,
  onInvitationAccepted?: (previousCount: number, currentCount: number) => void
) => {
  const httpClient = createHttpClient(apiClientWH);
  const usersAdapter = new UsersApiAdapter(httpClient);
  const usersService = new Service(usersAdapter);

  // 🎯 Ref para guardar el número anterior de invitaciones (debe estar en el nivel superior)
  const previousCountRef = useRef<number | null>(null);
  // 🎯 Ref para guardar los IDs de las invitaciones anteriores para comparar
  const previousInvitationIdsRef = useRef<number[]>([]);

  const {
    data: invitations,
    isLoading,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
  } = useQuery({
    queryKey: PENDING_INVITATIONS_QUERY_KEY,
    queryFn: () => usersService.getPendingInvitations(),
    staleTime: enablePolling ? 0 : 2 * 60 * 1000, // Si hay polling, no usar staleTime
    gcTime: 5 * 60 * 1000, // 5 minutos
    retry: false, // No reintentar si hay error de permisos
    refetchInterval: enablePolling ? 3000 : false, // Polling cada 3 segundos si está habilitado
    refetchIntervalInBackground: false, // No hacer polling cuando la pestaña está en background
  });

  // 🎯 Detectar cuando una invitación fue aceptada (disminuye el número)
  useEffect(() => {
    if (!enablePolling || !invitations || isLoading) return;

    const currentCount = invitations.length;
    const currentInvitationIds = invitations.map((inv) => inv.id);

    // Si es la primera vez que tenemos datos, guardar el count y los IDs
    if (previousCountRef.current === null) {
      previousCountRef.current = currentCount;
      previousInvitationIdsRef.current = currentInvitationIds;
      return;
    }

    // Si el número disminuyó, verificar si fue por cancelación o aceptación
    if (previousCountRef.current > currentCount) {
      // 🎯 Verificar si se está cancelando una invitación (flag global)
      // Si estamos cancelando, no detectar como aceptación
      const isCancelling = typeof globalThis !== 'undefined' && globalThis.__isCancellingInvitation === true;
      
      if (isCancelling) {
        // Actualizar los refs y salir sin llamar al callback
        previousCountRef.current = currentCount;
        previousInvitationIdsRef.current = currentInvitationIds;
        return;
      }

      // Verificar qué invitación desapareció
      const previousIds = previousInvitationIdsRef.current;
      const disappearedId = previousIds.find((id) => !currentInvitationIds.includes(id));

      // Si una invitación desapareció y NO fue por cancelación, asumir que fue aceptada
      if (disappearedId !== undefined) {
        // Llamar al callback si existe
        if (onInvitationAccepted) {
          onInvitationAccepted(previousCountRef.current, currentCount);
        }
      }
    }

    // Actualizar los refs
    previousCountRef.current = currentCount;
    previousInvitationIdsRef.current = currentInvitationIds;
  }, [invitations, enablePolling, isLoading, onInvitationAccepted]);

  return {
    invitations: invitations || [],
    isLoading,
    error,
    refetch,
    isFetching,
  };

  return {
    invitations: invitations || [],
    isLoading,
    error,
    refetch,
    isFetching,
  };
};


